import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Fuel, Search, Filter, ChevronRight, Settings, X } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const Cars = () => {
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    seats: '',
    transmission: '',
    fuelType: '',
    minPrice: '',
    maxPrice: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCars();
  }, []);

  useEffect(() => {
    filterCars();
  }, [searchTerm, filters, cars]);

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/cars`);
      setCars(response.data);
      setFilteredCars(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cars:', error);
      setLoading(false);
    }
  };

  const filterCars = () => {
    let filtered = [...cars];

    if (searchTerm) {
      filtered = filtered.filter(car => 
        car.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        car.brand?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.seats) {
      filtered = filtered.filter(car => car.seats === parseInt(filters.seats));
    }

    if (filters.transmission) {
      filtered = filtered.filter(car => car.transmission === filters.transmission);
    }

    if (filters.fuelType) {
      filtered = filtered.filter(car => car.fuel_type === filters.fuelType);
    }

    setFilteredCars(filtered);
  };

  const clearFilters = () => {
    setFilters({
      seats: '',
      transmission: '',
      fuelType: '',
      minPrice: '',
      maxPrice: ''
    });
    setSearchTerm('');
  };

  const getPriceDisplay = (car) => {
    // If there's a price range with min and max
    if (car.min_price && car.max_price && car.min_price !== car.max_price) {
      // Check if they are numeric values
      const isNumericMin = !isNaN(parseFloat(String(car.min_price).replace(/,/g, '')));
      const isNumericMax = !isNaN(parseFloat(String(car.max_price).replace(/,/g, '')));
      
      if (isNumericMin && isNumericMax) {
        const minNum = parseFloat(String(car.min_price).replace(/,/g, ''));
        const maxNum = parseFloat(String(car.max_price).replace(/,/g, ''));
        return {
          type: 'range',
          display: `${minNum.toLocaleString()} - ${maxNum.toLocaleString()} RWF`
        };
      } else {
        return {
          type: 'text',
          display: `${car.min_price} - ${car.max_price}`
        };
      }
    } 
    // If only base price exists
    else if (car.base_price) {
      const isNumeric = !isNaN(parseFloat(String(car.base_price).replace(/,/g, '')));
      if (isNumeric) {
        const num = parseFloat(String(car.base_price).replace(/,/g, ''));
        return {
          type: 'single',
          display: `${num.toLocaleString()} RWF`
        };
      } else {
        return {
          type: 'text',
          display: car.base_price
        };
      }
    }
    // Default fallback
    return {
      type: 'text',
      display: 'Price on Request'
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 bg-dark-800">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="bg-dark-800 min-h-screen py-10">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Luxury Fleet</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Choose from our selection of premium vehicles for your journey in Rwanda
          </p>
        </motion.div>

        {/* Search and Filter Bar */}
        <div className="glass-card rounded-lg p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by car name or brand..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-dark-700 rounded-lg hover:bg-dark-600 transition"
            >
              <Filter size={20} className="text-gray-400" />
              <span className="text-gray-300">Filters</span>
            </button>
            {(searchTerm || filters.seats || filters.transmission || filters.fuelType) && (
              <button
                onClick={clearFilters}
                className="flex items-center justify-center space-x-2 px-6 py-3 text-red-400 hover:bg-red-500/10 rounded-lg transition"
              >
                <X size={20} />
                <span>Clear All</span>
              </button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-dark-600 grid md:grid-cols-3 gap-4"
            >
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Seats</label>
                <select
                  value={filters.seats}
                  onChange={(e) => setFilters({...filters, seats: e.target.value})}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                >
                  <option value="">All</option>
                  <option value="4">4 Seats</option>
                  <option value="5">5 Seats</option>
                  <option value="7">7 Seats</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Transmission</label>
                <select
                  value={filters.transmission}
                  onChange={(e) => setFilters({...filters, transmission: e.target.value})}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                >
                  <option value="">All</option>
                  <option value="Automatic">Automatic</option>
                  <option value="Manual">Manual</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Fuel Type</label>
                <select
                  value={filters.fuelType}
                  onChange={(e) => setFilters({...filters, fuelType: e.target.value})}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                >
                  <option value="">All</option>
                  <option value="Petrol">Petrol</option>
                  <option value="Diesel">Diesel</option>
                </select>
              </div>
            </motion.div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-400">
            Found <span className="font-bold text-orange-500">{filteredCars.length}</span> vehicles
          </p>
        </div>

        {/* Cars Grid */}
        {filteredCars.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No vehicles match your criteria</p>
            <button onClick={clearFilters} className="mt-4 text-orange-500 hover:underline">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCars.map((car, index) => {
              const priceInfo = getPriceDisplay(car);
              return (
                <motion.div
                  key={car.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -10 }}
                  className="glass-card rounded-xl overflow-hidden hover:shadow-elegant-lg transition-all duration-300"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={car.image_url} 
                      alt={car.name} 
                      className="w-full h-full object-cover hover:scale-110 transition duration-500"
                    />
                    {car.featured && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-white mb-1">{car.name}</h3>
                    <p className="text-gray-400 text-sm mb-3">{car.brand}</p>
                    
                    <div className="flex justify-between items-center py-3 border-y border-dark-600 my-3">
                      <div className="flex items-center space-x-3 text-sm text-gray-400">
                        <span className="flex items-center space-x-1">
                          <Users size={14} className="text-orange-500" />
                          <span>{car.seats} Seats</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Settings size={14} className="text-orange-500" />
                          <span>{car.transmission}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Fuel size={14} className="text-orange-500" />
                          <span>{car.fuel_type}</span>
                        </span>
                      </div>
                    </div>

                    {/* Price Display */}
                    <div className="mb-4 text-center">
                      {priceInfo.type === 'range' ? (
                        <>
                          <div className="text-2xl font-bold text-orange-500">
                            {priceInfo.display}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">price range</p>
                        </>
                      ) : priceInfo.type === 'single' ? (
                        <>
                          <div className="text-2xl font-bold text-orange-500">
                            {priceInfo.display}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">per day</p>
                        </>
                      ) : (
                        <>
                          <div className="text-xl font-semibold text-orange-500">
                            {priceInfo.display}
                          </div>
                        </>
                      )}
                    </div>

                    <Link 
                      to={`/cars/${car.id}`}
                      className="flex items-center justify-center space-x-2 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition group w-full"
                    >
                      <span>View Details</span>
                      <ChevronRight size={16} className="group-hover:translate-x-1 transition" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Cars;